import React, { FunctionComponent, useEffect, useState } from "react";

import { Editor } from "@monaco-editor/react";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum } from "utils/enums";
import { IDataConfigLocal, ITableConfigProp } from "../common/api/interfaces/interfaces";
import DatasetConfigEditor from "./dataset-config-editor/DatasetConfigEditor";

const defaultDataInputOutputTableConfig: ITableConfigProp = {
  config: {
    alignStartTo: DweenTimeUnitEnum.HOURS,
    timeOffset: 0,
    timeOffsetUnits: DweenTimeUnitEnum.HOURS,
    dataFrequency: DweenTimeUnitEnum.HOURS,
    updateFrequency: 5,
    maxHeight: 450,
  },
  datasets: [],
};

type DataInputOutputTableEditorProps = {
  config?: ITableConfigProp;
  onConfigChange: (config: ITableConfigProp) => void;
};

function DataInputOutputTableEditor({ config, onConfigChange }: DataInputOutputTableEditorProps) {
  let conf: ITableConfigProp = config ?? defaultDataInputOutputTableConfig;

  if (Object.keys(conf).length === 0) {
    conf = defaultDataInputOutputTableConfig;
  }

  const [currentConfig, setCurrentConfig] = useState<ITableConfigProp>(conf);
  const [datasetConfigEditorIsVisible, setDatasetConfigEditorVisibility] = useState(false);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const handleDatasetConfigEditClick = () => {
    setDatasetConfigEditorVisibility(true);
  };

  const handleConfigChange = (key: keyof IDataConfigLocal, value: number | string | boolean | null) => {
    setCurrentConfig(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));
  };

  return (
    <div>
      <div className="grid p-fluid">
        <div className="flex flex-row w-12 justify-content-between">
          <Button
            icon="pi pi-list"
            type="button"
            label="Datasets list"
            className="component-editor-button icon-blue w-2 align-self-center"
            onClick={() => handleDatasetConfigEditClick()}
          />
          <div className="flex flex-row gap-2 w-4">
            <div className="w-6 gap-2 flex flex-column align-items-center">
              <label htmlFor="displayNote">{T("action.add-note")}</label>
              <InputSwitch
                id="displayNote"
                checked={Boolean(currentConfig.config?.displayNote)}
                onChange={e => handleConfigChange("displayNote", e.value)}
              />
            </div>
            <div className="w-6 gap-2 flex flex-column align-items-center">
              <label htmlFor="hideUpdateButton">{T("action.hide-update-button")}</label>
              <InputSwitch
                id="hideUpdateButton"
                checked={Boolean(currentConfig.config?.hideUpdateButton)}
                onChange={e => handleConfigChange("hideUpdateButton", e.value)}
              />
            </div>
          </div>
          <DatasetConfigEditor
            currentConfig={currentConfig}
            setCurrentConfig={setCurrentConfig}
            onConfigChange={onConfigChange}
            datasetConfigEditorIsVisible={datasetConfigEditorIsVisible}
            setDatasetConfigEditorVisibility={setDatasetConfigEditorVisibility}
          />
        </div>
        <Divider />
        <div className="flex w-12">
          <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="timeOffset">{T("load-conf.time-offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="timeOffset"
                className="w-6"
                value={currentConfig.config?.timeOffset as number}
                onChange={e => handleConfigChange("timeOffset", e.value)}
              />
              <TimeUnitDropdown
                id="timeOffsetUnits"
                className="p-inputgroup-addon p-0 w-6"
                value={currentConfig.config?.timeOffsetUnits ? currentConfig.config.timeOffsetUnits : ""}
                onChange={e => handleConfigChange("timeOffsetUnits", e.value.toString().toUpperCase())}
              />
            </div>
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="dataFrequency">{T("config.data-frequency")}</label>
            <TimeUnitDropdown
              id="dataFrequency"
              className="p-0"
              value={currentConfig.config?.dataFrequency ? currentConfig.config.dataFrequency : ""}
              onChange={e => handleConfigChange("dataFrequency", e.value.toString().toUpperCase())}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="alignStartTo">{T("load-conf.align-start-to")}</label>
            <TimeUnitDropdown
              id="alignStartTo"
              className="p-0"
              value={currentConfig.config?.alignStartTo ? currentConfig.config.alignStartTo : ""}
              onChange={e => handleConfigChange("alignStartTo", e.value.toString().toUpperCase())}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="updateFrequency"
                value={currentConfig.config?.updateFrequency as number}
                onChange={e => handleConfigChange("updateFrequency", e.value)}
              />
              <span className="p-inputgroup-addon">s</span>
            </div>
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="maxHeight">{T("config.max-height")}</label>
            <div className="p-inputgroup w-full">
              <InputNumber
                id="maxHeight"
                value={currentConfig.config?.maxHeight as number}
                onChange={e => handleConfigChange("maxHeight", e.value)}
              />
              <span className="p-inputgroup-addon">px</span>
            </div>
          </div>
        </div>
        {currentConfig.config?.displayNote && (
          <>
            <Divider />
            <div className="w-12 flex flex-column gap-2">
              <label htmlFor="note">{T("word.note")}</label>
              <Editor
                height="30rem"
                value={currentConfig.config?.note}
                onChange={value => handleConfigChange("note", value ?? "")}
                defaultLanguage="html"
                className="border-1 border-300 border-round overflow-hidden"
                options={{
                  lineNumbers: "off",
                  lineDecorationsWidth: 0,
                  minimap: {
                    enabled: false,
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DataInputOutputTableEditor as FunctionComponent;
